import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  Chip,
  Fade,
  Zoom,
} from '@mui/material';
import { Edit, Delete, Visibility, Person, Send } from '@mui/icons-material';

export interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: any, row?: any) => string | React.ReactNode;
}

export interface DataTableProps {
  columns: Column[];
  rows: any[];
  title?: string;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  /** Optional “send now” for campaigns / similar rows */
  onSend?: (row: any) => void;
  loading?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  title,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
  onSend,
  loading = false,
}) => {
  // Ensure totalCount is always a valid number
  const safeTotalCount = Math.max(0, totalCount || 0);
  const safeRowsPerPage = Math.max(1, rowsPerPage || 10);
  // Ensure page is within valid bounds
  const maxPage = Math.max(0, Math.ceil(safeTotalCount / safeRowsPerPage) - 1);
  const safePage = Math.min(Math.max(0, page || 0), maxPage);

  const handleChangePage = (_event: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  return (
    <Paper 
      sx={{ 
        width: '100%', 
        overflow: 'hidden',
        borderRadius: 4,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(232, 180, 184, 0.2)',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
      }}
    >
      {title && (
        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {title}
          </Typography>
        </Box>
      )}
      
      <TableContainer sx={{ 
        maxHeight: { xs: 'calc(100vh - 400px)', sm: 440 },
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: 8,
          width: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(232, 180, 184, 0.1)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#E8B4B8',
          borderRadius: 4,
          '&:hover': {
            backgroundColor: '#D4969B',
          },
        },
      }}>
        <Table stickyHeader aria-label="data table" sx={{ minWidth: { xs: 600, sm: 750 } }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 100%)',
                    color: '#5C2A30',
                    fontWeight: 800,
                    fontSize: '0.8125rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    borderBottom: '1px solid rgba(92, 42, 48, 0.18)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {(onView || onEdit || onDelete || onSend) && (
                <TableCell 
                  align="center"
                  sx={{
                    background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 100%)',
                    color: '#5C2A30',
                    fontWeight: 800,
                    fontSize: '0.8125rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    borderBottom: '1px solid rgba(92, 42, 48, 0.18)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 8 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        border: '4px solid #E8B4B8',
                        borderTop: '4px solid transparent',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                    <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                      Loading customers...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 8 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'rgba(232, 180, 184, 0.1)',
                        color: '#E8B4B8',
                      }}
                    >
                      <Person sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography color="text.secondary" variant="h6" sx={{ fontWeight: 500 }}>
                      No customers found
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Try adjusting your search criteria or add a new customer
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <Fade in timeout={300} style={{ transitionDelay: `${index * 50}ms` }} key={index}>
                  <TableRow 
                    hover 
                    role="checkbox" 
                    tabIndex={-1}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(232, 180, 184, 0.08)',
                        transform: 'translateX(4px)',
                        boxShadow: '0 4px 20px rgba(232, 180, 184, 0.15)',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(232, 180, 184, 0.02)',
                      },
                    }}
                  >
                    {columns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell 
                          key={column.id} 
                          align={column.align}
                          sx={{
                            borderBottom: '1px solid rgba(232, 180, 184, 0.1)',
                            py: 2,
                            fontSize: '0.9rem',
                            fontWeight: 500,
                          }}
                        >
                          {column.format ? column.format(value, row) : value}
                        </TableCell>
                      );
                    })}
                    {(onView || onEdit || onDelete || onSend) && (
                      <TableCell 
                        align="center"
                        sx={{
                          borderBottom: '1px solid rgba(232, 180, 184, 0.1)',
                          py: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          {onSend && (
                            <Tooltip title="Send campaign" arrow>
                              <IconButton
                                size="small"
                                onClick={() => onSend(row)}
                                disabled={row.status === 'sent' || row.status === 'cancelled'}
                                sx={{
                                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                                  color: '#10b981',
                                  border: '1px solid rgba(16, 185, 129, 0.2)',
                                  '&:hover': {
                                    bgcolor: '#10b981',
                                    color: 'white',
                                  },
                                }}
                              >
                                <Send sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onView && (
                            <Zoom in timeout={300} style={{ transitionDelay: `${index * 50 + 100}ms` }}>
                              <Tooltip title="View Details" arrow>
                                <IconButton 
                                  size="small" 
                                  onClick={() => onView(row)}
                                  sx={{
                                    bgcolor: 'rgba(79, 172, 254, 0.1)',
                                    color: '#4facfe',
                                    border: '1px solid rgba(79, 172, 254, 0.2)',
                                    '&:hover': {
                                      bgcolor: '#4facfe',
                                      color: 'white',
                                      transform: 'scale(1.1)',
                                      boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  <Visibility sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Zoom>
                          )}
                          {onEdit && (
                            <Zoom in timeout={300} style={{ transitionDelay: `${index * 50 + 150}ms` }}>
                              <Tooltip title="Edit Customer" arrow>
                                <IconButton 
                                  size="small" 
                                  onClick={() => onEdit(row)}
                                  sx={{
                                    bgcolor: 'rgba(232, 180, 184, 0.1)',
                                    color: '#E8B4B8',
                                    border: '1px solid rgba(232, 180, 184, 0.2)',
                                    '&:hover': {
                                      bgcolor: '#E8B4B8',
                                      color: 'white',
                                      transform: 'scale(1.1)',
                                      boxShadow: '0 4px 12px rgba(232, 180, 184, 0.3)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  <Edit sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Zoom>
                          )}
                          {onDelete && (
                            <Zoom in timeout={300} style={{ transitionDelay: `${index * 50 + 200}ms` }}>
                              <Tooltip title="Delete Customer" arrow>
                                <IconButton 
                                  size="small" 
                                  onClick={() => onDelete(row)}
                                  sx={{
                                    bgcolor: 'rgba(245, 101, 101, 0.1)',
                                    color: '#f56565',
                                    border: '1px solid rgba(245, 101, 101, 0.2)',
                                    '&:hover': {
                                      bgcolor: '#f56565',
                                      color: 'white',
                                      transform: 'scale(1.1)',
                                      boxShadow: '0 4px 12px rgba(245, 101, 101, 0.3)',
                                    },
                                    transition: 'all 0.3s ease',
                                  }}
                                >
                                  <Delete sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Zoom>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                </Fade>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(232, 180, 184, 0.05) 0%, rgba(245, 213, 216, 0.05) 100%)',
          borderTop: '1px solid rgba(232, 180, 184, 0.1)',
        }}
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={safeTotalCount}
          rowsPerPage={safeRowsPerPage}
          page={safePage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            '& .MuiTablePagination-toolbar': {
              px: 3,
              py: 2,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontWeight: 500,
              color: 'text.primary',
            },
            '& .MuiTablePagination-select': {
              borderRadius: 2,
              border: '1px solid rgba(232, 180, 184, 0.3)',
              '&:hover': {
                borderColor: '#E8B4B8',
              },
            },
            '& .MuiTablePagination-actions button': {
              color: '#E8B4B8',
              '&:hover': {
                backgroundColor: 'rgba(232, 180, 184, 0.1)',
              },
              '&.Mui-disabled': {
                color: 'rgba(232, 180, 184, 0.3)',
              },
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default DataTable;